import * as React from "react";
import { useFormContext } from "react-hook-form";
import { format, parseISO } from "date-fns";
import { SVGs } from "../../assets";
import { Button, Card, Divider, Text } from "../../components";
import { Tools } from "../../utils";
import { twMerge } from "tailwind-merge";
import PaymentView from "../Payment/view";
import { useAuthStore, useSnackBar } from "../../stores";
import { BookingPartnerLocation } from "../../apis/products";

type BookingSummaryCardProps = {
  t: (key: string) => string;
  summary: {
    location: BookingPartnerLocation | null;
    [key: string]: any;
  };
  onContinue: () => void;
};

function SummaryRow({
  label,
  value,
  className,
  valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
  valueClassName?: string;
}) {
  return (
    <div
      className={twMerge(
        "flex items-center justify-between gap-[12px]",
        className,
      )}
    >
      <Text className="font-[600]">{label}</Text>
      <Text className={twMerge("font-[600]", valueClassName)}>{value}</Text>
    </div>
  );
}

export function BookingSummaryCard(props: BookingSummaryCardProps) {
  const form = useFormContext();
  const { showSnackbar } = useSnackBar();
  const { user } = useAuthStore();
  const [serviceDate, serviceTime] = form.watch(["serviceDate", "serviceTime"]);

  return (
    //top is 56px (breadcrumbs) + 80px (header) + 20px (padding)
    <Card className="lg:sticky top-[156px] z-10 p-0 overflow-hidden border border-border">
      <div className="bg-secondary p-[24px] space-y-[9px]">
        <Text level={1} className="text-text-surface">
          {props.t("general.bookingSummary")}
        </Text>
        <Text level={4} className="text-text-surface">
          <Text as="span" level={4} className="font-[700]">
            {props.t("general.ref")}
          </Text>{" "}
          {props.t("booking.pendingCreation")}
        </Text>
      </div>
      <div className="p-[20px] space-y-[16px]">
        {!!props.summary?.location && (
          <PaymentView.SummaryRow
            icon={SVGs.locationMarker}
            label={props.summary.location.code}
            value={
              props.summary.location.nearbyPlaces?.[0]?.name ??
              props.summary.location.city?.name
            }
          />
        )}
        <PaymentView.SummaryRow
          label={props.summary.service.name}
          icon={SVGs.shield}
        />
        {[
          ...(!!serviceDate && !!serviceTime
            ? [
                {
                  divider: true,
                  label: props.t("booking.date"),
                  value: format(parseISO(serviceDate), "MMM d, yyyy"),
                },
                {
                  label: props.t("booking.time"),
                  value: serviceTime,
                },
              ]
            : []),
          {
            divider: true,
            label: `${props.t("booking.originalRate")} (${props.summary.orderCount}x)`,
            value: Tools.formatEur(props.summary.originalPrice),
            valueClassName: !!props.summary?.discountedPrice
              ? "line-through"
              : "",
          },
          {
            rowComp: !props.summary?.discountedPrice ? (
              <div />
            ) : (
              <div className="rounded-[6px]  bg-primary px-[14px] h-[50px] flex items-center justify-between">
                <Text className="font-[700]">
                  {props.t("booking.tpaRateApplied")}
                </Text>
                <Text className="font-[700]">
                  {Tools.formatEur(props.summary.finalPrice)}
                </Text>
              </div>
            ),
          },
          {
            divider: true,
            label: props.t(
              user?.paymentRequired ? "general.payableAmount" : "general.billableAmount",
            ),
            value: Tools.formatEur(props.summary.finalPrice),
          },
        ].map((rowProps, idx) => (
          <>
            {rowProps.divider && <Divider />}
            {rowProps.rowComp || (
              <SummaryRow key={rowProps.label ?? idx} {...rowProps} />
            )}
          </>
        ))}
        {!user?.paymentRequired && (
          <div className="rounded-[6px] border border-border bg-background p-[12px]">
            <Text className="text-center">
              {props.t("booking.invoicedLaterNote")}
            </Text>
          </div>
        )}
        <div className="rounded-[6px] border border-border bg-background p-[12px]">
          <Text className="text-center">
            {props.t("booking.liabilityDisclaimer")}
          </Text>
        </div>
        <Button
          color="primary"
          className="w-full justify-center"
          children={
            user?.paymentRequired
              ? props.t("general.proceedToPayment")
              : props.t("general.reviewAndBook")
          }
          icon={
            !user?.paymentRequired ? (
              <SVGs.LeftArrow className="w-[16px] h-[16px] rotate-180" />
            ) : (
              <SVGs.LeftArrow className="w-[16px] h-[16px] rotate-180" />
            )
          }
          onClick={form.handleSubmit(
            () => {
              form.setValue("termsAccepted", false);
              form.clearErrors("termsAccepted");
              props.onContinue();
            },
            () => {
              showSnackbar("Please resolved highlighted fields", {
                type: "error",
              });
            },
          )}
        />
      </div>
    </Card>
  );
}
